import React, { useRef, useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';

/**
 * A GDS (GEICO Design System) InputBox component that integrates with React Hook Form.
 * This component uses the gds-input-wrapper web component and provides form validation
 * and state management through react-hook-form. It handles both masked and unmasked inputs,
 * using MutationObserver to detect when iMask instances become available for masked inputs.
 * 
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} [props.name="InputBox"] - The field name for form registration and validation
 * @param {string} [props.type="text"] - The input type (text, password, email, number, etc.)
 * @param {string|React.ReactNode} [props.label="Input Box"] - Label text or JSX to display above the input
 * @param {string} [props.description] - Optional description text to display below the label
 * @param {string} [props.placeholder] - Optional placeholder text for the input
 * @param {boolean} [props.smallViewSmallInput=false] - Sets the input width to a small size on small viewports
 * @param {boolean} [props.fullWidth=false] - Sets the input to full width
 * @param {string} [props.prefixText] - Sets the prefix text that appears before the input
 * @param {string} [props.suffixText] - Sets the suffix text that appears after the input
 * @param {"small"|"medium"|"large"|number} [props.size="medium"] - Sets the size of the input element
 * @param {string} [props.maskType="none"] - Sets the input mask type (phone, ssn, fein, dob, etc.)
 * @param {string} [props.mask] - Optional custom mask pattern for secure input masking
 * @param {React.ReactNode} [props.leftVisual] - Adds a leading icon/image to the input
 * @param {React.ReactNode} [props.rightVisual] - Adds a trailing icon/image to the input
 * @param {boolean} [props.includeVisibilityToggle] - Whether to include a password visibility toggle for password fields
 * 
 * @returns {JSX.Element} The rendered InputBox component
 * 
 * @example
 * // Basic usage
 * <InputBox
 *   control={control}
 *   name="firstName"
 *   label="First Name"
 *   placeholder="Enter your first name"
 * />
 * 
 * @example
 * // With mask and description
 * <InputBox
 *   control={control}
 *   name="phone"
 *   type="tel"
 *   label="Phone Number"
 *   description="Enter your 10-digit phone number"
 *   maskType="phone"
 *   placeholder="(___) ___-____"
 * />
 * 
 * @example
 * // With custom mask pattern
 * <InputBox
 *   control={control}
 *   name="producerNPN"
 *   label="National Producer Number"
 *   mask="000000000"
 *   placeholder="NPN"
 * />
 * 
 * @example
 * // With JSX label containing conditional content
 * <InputBox
 *   control={control}
 *   name="amount"
 *   label={<>Amount {isRequired && <span className="required">*</span>}</>}
 *   prefixText="$"
 *   suffixText=".00"
 *   size="small"
 * />
 */
export default function InputBox({
    control,
    name = "InputBox",
    type = "text",
    label = "Input Box",
    description,
    placeholder,
    smallViewSmallInput = false,
    fullWidth = false,
    prefixText,
    suffixText,
    size = "medium",
    maskType = "none",
    mask,
    maskOptions,
    leftVisual,
    rightVisual,
    includeVisibilityToggle
}) {
    /** @type {React.RefObject<HTMLInputElement>} Reference to the native input DOM element */
    const inputRef = useRef();

    /** @type {React.RefObject<HTMLElement>} Reference to the gds-input-wrapper DOM element */
    const inputWrapperRef = useRef();

    /** @type {React.RefObject<string>} Unique ID for the input element */
    const inputId = useRef(`input-box-${v4()}`);

    /** 
     * State to track whether the iMask instance has been instantiated and is ready for use.
     * Used to determine when to switch from standard input handling to masked input handling.
     * @type {boolean} - true when iMask instance is available, false otherwise
     */
    const [iMaskInstantiated, setIMaskInstantiated] = useState(false);

    /**
     * At the time of writing, <gds-input-wrapper> has two internal instances of iMask: _iMaskInstance and
     * _secureMaskInstance. The getIMaskInstance() function documented in the Hue storybook only returns the
     * _iMaskInstance, but often the _secureMaskInstance is the one that is actually used.
     */
    const getIMaskInstance = () =>
        inputWrapperRef.current?.getIMaskInstance?.() ||
        inputWrapperRef.current?._secureMaskInstance;

    /**
     * React Hook Form controller that provides field state and handlers
     * @type {Object}
     * @property {Object} field - Field handlers and value
     * @property {Function} field.onChange - Handler for value changes
     * @property {Function} field.onBlur - Handler for blur events
     * @property {any} field.value - Current field value
     * @property {Object} fieldState - Field validation state
     * @property {Object} fieldState.error - Current validation error (if any)
     */
    const {
        field: { onChange, onBlur, value },
        fieldState: { error }
    } = useController({
        name,
        control
    });

    /**
     * Effect hook that uses MutationObserver to detect when iMask instances become available.
     * This is necessary because iMask initialization in gds-input-wrapper can be asynchronous.
     * Only runs when mask or maskType props indicate masking should be active.
     * 
     * @effect
     * @dependencies [mask, maskType] - Re-runs when masking configuration changes
     */
    useEffect(() => {
        if (mask || (maskType && maskType !== "none")) {
            const inputWrapperElement = inputWrapperRef.current;
            if (!inputWrapperElement) return;

            const observer = new MutationObserver(() => {
                const iMaskInstance = getIMaskInstance();

                if (iMaskInstance) {
                    observer.disconnect(); // Stop observing once found

                    setIMaskInstantiated(true);
                }
            });

            observer.observe(inputWrapperElement, {
                childList: true,
                subtree: true,
                attributes: true
            });

            return () => observer.disconnect();
        }
    }, [mask, maskType]);

    /**
     * Effect hook to set up event listeners for the native input element within gds-input-wrapper.
     * Handles both masked and unmasked input scenarios:
     * - For masked inputs: Uses iMask's unmasked value for form state
     * - For unmasked inputs: Uses standard input value
     * Cleans up event listeners on component unmount or dependency changes.
     * 
     * @effect
     * @listens input - Standard input event fired when input value changes
     * @listens blur - Standard blur event for field validation
     */
    useEffect(() => {
        /**
         * Handles the native input change event and updates the form field value
         * @param {Event} e - The standard input event from the input element
         */
        const handleInputEvent = (e) => {
            const inputValue = e?.target?.value;

            if (iMaskInstantiated) {
                const iMask = getIMaskInstance();

                if (maskOptions) {
                    iMask.updateOptions(maskOptions);
                }

                iMask.value = inputValue;
                onChange(iMask?.unmaskedValue);
            } else {
                onChange(inputValue);
            }
        };

        const inputElement = inputRef.current;

        if (inputElement) {
            inputElement.addEventListener("input", handleInputEvent);
            inputElement.addEventListener("blur", onBlur);

            // Only initialize an empty value; do not replay non-empty mount values because that can
            // overwrite parent-driven resets during field mode switches (for example US -> non-US ZIP).
            if (value === "" && inputElement.value === "") {
                const inputEvent = new Event('input', { bubbles: true });
                inputElement.dispatchEvent(inputEvent);
            }

            return () => {
                inputElement.removeEventListener("input", handleInputEvent);
                inputElement.removeEventListener("blur", onBlur);
            };
        }
    }, [onChange, onBlur, iMaskInstantiated, value]);

    useEffect(() => {
        if (iMaskInstantiated && maskOptions) {
            getIMaskInstance()?.updateOptions(maskOptions);
        }
    }, [iMaskInstantiated, maskOptions]);

    useEffect(() => {
        if (value !== "") {
            return;
        }

        const iMask = getIMaskInstance();

        if (iMask && iMask.value !== "") {
            iMask.value = "";
        }

        if (inputRef.current && inputRef.current.value !== "") {
            inputRef.current.value = "";
            inputWrapperRef.current?.syncInputValue?.();
        }
    }, [value, iMaskInstantiated]);

    return (
        <gds-input-wrapper
            ref={inputWrapperRef}
            small-view-small-input={smallViewSmallInput}
            full-width={fullWidth}
            prefix-text={prefixText}
            suffix-text={suffixText}
            size={size}
            mask-type={maskType}
            mask={mask}
        >
            {/* Conditionally render label if provided */}
            {label && (
                <gds-label-legend slot="label" element="label" for={inputId.current}>
                    {label}

                    {description && (
                        <span slot="description">{description}</span>
                    )}
                </gds-label-legend>
            )}

            {/* Conditionally render left visual if provided */}
            {leftVisual}

            <input
                slot="input"
                id={inputId.current}
                ref={inputRef}
                name={name}
                type={type}
                placeholder={placeholder}
                defaultValue={value}
            />

            {/* Conditionally render right visual if provided */}
            {rightVisual}

            {includeVisibilityToggle &&
                <gds-input-value-visibility-toggle
                    input-name={name}
                    slot="right-visual"
                />
            }

            {/* Conditionally render error message if validation error exists */}
            {error && (
                <span slot="error-message">{error.message}</span>
            )}
        </gds-input-wrapper>
    );
}